
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import { userAPI } from '../../UserAPI';
import { AdvertisementColumn, Billboard, Building, ChargingStation, EntityQuery, Filter, PoI, Query, University, calculateBoundingBox } from '@the-real-insight/tri-model';

import { cn } from '@/lib/utils';
import { Checkbox } from '@/components/ui/checkbox';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandItem,
    CommandList,
} from '@/components/ui/command';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover';
import { Slider } from '@/components/ui/slider';

class Properties {
    object: any;
    poisChanged?: (pois: any[]) => void
}

/**
 * Auswahlliste der Ortsarten. Die vier Überschriften (`header: true`) waren im
 * Fluent-Bestand `DropdownMenuItemType.Header`; hier tragen sie die
 * Gruppenüberschrift der Befehlsliste und sind — anders als im Shim, der sie
 * versehentlich als anklickbare Einträge zeigte — nicht mehr auswählbar. Keiner
 * ihrer Schlüssel wird unten ausgewertet.
 */
const poiGroups: { heading: string; items: { key: string; text: string }[] }[] = [
    {
        heading: 'Infrastructur',
        items: [
            { key: 'ChargingStation', text: 'Ladestationen' },
            { key: 'BusStop', text: 'Bushaltestelle' },
        ],
    },
    {
        heading: 'Versorgung',
        items: [
            { key: 'supermarket', text: 'Supermarkt' },
        ],
    },
    {
        heading: 'Bildung',
        items: [
            { key: 'Kindergarten', text: 'Kindergärten' },
            { key: 'School', text: 'Schulen' },
            { key: 'University', text: 'Universitäten' },
        ],
    },
    {
        heading: 'Werbung',
        items: [
            { key: 'AdvertisementColumn', text: 'Litfaßsäulen' },
            { key: 'Billboard', text: 'Anzeigetafeln' },
        ],
    },
];

const poiOptions = poiGroups.flatMap((group) => group.items);

/** Kundenfarben wie die früheren App.sass-Klassen, Fallback auf die Tokens. */
const PRIMARY_TEXT = 'text-[color:var(--customer-primary,hsl(var(--foreground)))]';
const PRIMARY_LIGHT_TEXT = 'text-[color:var(--customer-primary-light,hsl(var(--muted-foreground)))]';
const PRIMARY_DARK_TEXT = 'text-[color:var(--customer-primary-dark,hsl(var(--foreground)))]';
const SECONDARY_TEXT = 'text-[color:var(--customer-secondary,hsl(var(--foreground)))]';
const SECONDARY_DARK_TEXT = 'text-[color:var(--customer-secondary-dark,hsl(var(--foreground)))]';

export default function PoIProperties(properties: Properties) {
    const { t: _t } = useTranslation();
    const [lowerDistance, setLowerDistance] = useState(0);
    const [higherDistance, setHigherDistance] = useState(1000);
    const [finalHigherDistance, setFinalHigherDistance] = useState(1000);
    const [chargingStations, setChargingStations] = useState([]);
    const [universities, setUniversities] = useState([]);
    const [billboards, setBillboards] = useState([]);
    const [advertisementColumns, setAdvertisementColumns] = useState([]);
    const [selectedPOITypes, setSelectedPOITypes] = useState<string[]>([]);
    const [typesOpen, setTypesOpen] = useState(false);

    const onPOITypesSelectionChange = (key: string): void => {
        setSelectedPOITypes(
            selectedPOITypes.indexOf(key) < 0
                ? [...selectedPOITypes, key]
                : selectedPOITypes.filter((selected) => selected !== key),
        );
    };

    useEffect(() => {
        const call = async () => {
            const building = properties.object as Building;

            const boundingBox = calculateBoundingBox(building.shape);

            console.log(boundingBox);
            console.log(`${boundingBox.centerLat} ${boundingBox.centerLon} ${finalHigherDistance}`);

            const newChargingStations: any = await userAPI.query(new Query(new EntityQuery(ChargingStation.type, [], undefined,
                Filter.getContainsPointFilter(boundingBox.centerLat, boundingBox.centerLon, finalHigherDistance)
            )));

            console.log(newChargingStations);

            setChargingStations(newChargingStations);

            const newUniversities: any = await userAPI.query(new Query(new EntityQuery(University.type, [], undefined,
                Filter.getContainsPointFilter(boundingBox.centerLat, boundingBox.centerLon, finalHigherDistance)
            )));

            console.log(newUniversities);

            setUniversities(newUniversities);

            const newBillboards: any = await userAPI.query(new Query(new EntityQuery(Billboard.type, [], undefined,
                Filter.getContainsPointFilter(boundingBox.centerLat, boundingBox.centerLon, finalHigherDistance)
            )));

            console.log(newBillboards);

            setBillboards(newBillboards);

            const newAdvertismentColumns: any = await userAPI.query(new Query(new EntityQuery(AdvertisementColumn.type, [], undefined,
                Filter.getContainsPointFilter(boundingBox.centerLat, boundingBox.centerLon, finalHigherDistance)
            )));

            console.log(newAdvertismentColumns);

            setAdvertisementColumns(newAdvertismentColumns);

            if (properties.poisChanged) {
                let newPOIs: PoI[] = [];

                if (selectedPOITypes.indexOf('ChargingStation') >= 0) {
                    newPOIs = [...newPOIs, ...newChargingStations];
                }

                if (selectedPOITypes.indexOf('University') >= 0) {
                    newPOIs = [...newPOIs, ...newUniversities];
                }

                if (selectedPOITypes.indexOf('Billboard') >= 0) {
                    newPOIs = [...newPOIs, ...newBillboards];
                }

                if (selectedPOITypes.indexOf('AdvertisementColumn') >= 0) {
                    newPOIs = [...newPOIs, ...newAdvertismentColumns];
                }

                properties.poisChanged(newPOIs);
            }
        };

        call();
    }, [finalHigherDistance, selectedPOITypes]);

    const triggerLabel = selectedPOITypes.length === 0
        ? 'Bitte auswählen'
        : selectedPOITypes
            .map((key) => poiOptions.find((option) => option.key === key)?.text ?? key)
            .join(', ');

    return <div className="flex min-w-0 max-w-[1400px] flex-col gap-4">
        <h3 className="text-base font-semibold leading-tight">Dienste und Orte in der Nähe</h3>
        <Popover open={typesOpen} onOpenChange={setTypesOpen}>
            <PopoverTrigger
                role="combobox"
                aria-expanded={typesOpen}
                className="flex h-9 w-[300px] max-w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm outline-none transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 max-sm:h-11"
            >
                <span className={cn('line-clamp-1 text-left', selectedPOITypes.length === 0 && 'text-muted-foreground')}>
                    {triggerLabel}
                </span>
                <ChevronDown aria-hidden="true" className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </PopoverTrigger>
            <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                <Command>
                    <CommandList>
                        <CommandEmpty>No options</CommandEmpty>
                        {poiGroups.map((group) => (
                            <CommandGroup key={group.heading} heading={group.heading}>
                                {group.items.map((option) => (
                                    <CommandItem
                                        key={option.key}
                                        value={option.text}
                                        onSelect={() => onPOITypesSelectionChange(option.key)}
                                    >
                                        <Checkbox
                                            className="mr-2"
                                            checked={selectedPOITypes.indexOf(option.key) >= 0}
                                            onCheckedChange={() => onPOITypesSelectionChange(option.key)}
                                        />
                                        <span className="flex-1 truncate">{option.text}</span>
                                    </CommandItem>
                                ))}
                            </CommandGroup>
                        ))}
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
        <div className="flex min-w-0 max-w-md flex-col gap-1.5">
            <Slider
                min={0}
                max={10000}
                step={100}
                aria-label="Entfernung"
                value={[lowerDistance, higherDistance]}
                onValueChange={(range: number[]) => {
                    setLowerDistance(range[0]);
                    setHigherDistance(range[1]);
                }}
                onValueCommit={(range: number[]) => {
                    setFinalHigherDistance(range[1]);
                }}
            ></Slider>
            <div className="text-xs text-muted-foreground">{`${lowerDistance}m`} – {`${higherDistance}m`}</div>
        </div>
        {selectedPOITypes.indexOf('Kindergarten') >= 0
            ?
            <div className="flex flex-wrap items-baseline gap-2">
                <div className={`text-2xl font-bold ${PRIMARY_TEXT}`}>4</div><div className={`text-lg ${PRIMARY_TEXT}`}>Kindergärten</div>
            </div>
            :
            <></>
        }
        {selectedPOITypes.indexOf('BusStop') >= 0
            ?
            <div className="flex flex-wrap items-baseline gap-2">
                <div className={`text-2xl font-bold ${SECONDARY_TEXT}`}>2</div><div className={`text-lg ${SECONDARY_TEXT}`}>Bushaltestellen</div>
            </div>
            :
            <></>
        }
        {selectedPOITypes.indexOf('Supermarket') >= 0
            ?
            <div className="flex flex-wrap items-baseline gap-2">
                <div className={`text-2xl font-bold ${PRIMARY_LIGHT_TEXT}`}>3</div><div className={`text-lg ${PRIMARY_LIGHT_TEXT}`}>Supermärkte</div>
            </div>
            :
            <></>
        }
        {selectedPOITypes.indexOf('ChargingStation') >= 0
            ?
            <div className="flex flex-wrap items-baseline gap-2">
                <div className={`text-2xl font-bold ${PRIMARY_DARK_TEXT}`}>{chargingStations.length}</div><div className={`text-lg ${PRIMARY_DARK_TEXT}`}>Ladesäulen</div>
            </div>
            :
            <></>
        }
        {selectedPOITypes.indexOf('University') >= 0
            ?
            <div className="flex flex-wrap items-baseline gap-2">
                <div className={`text-2xl font-bold ${SECONDARY_DARK_TEXT}`}>{universities.length}</div><div className={`text-lg ${SECONDARY_DARK_TEXT}`}>Universitäten und Hochschulen</div>
            </div>
            :
            <></>
        }
        {selectedPOITypes.indexOf('Billboard') >= 0
            ?
            <div className="flex flex-wrap items-baseline gap-2">
                <div className={`text-2xl font-bold ${PRIMARY_LIGHT_TEXT}`}>{billboards.length}</div><div className={`text-lg ${PRIMARY_LIGHT_TEXT}`}>Anzeigetafeln</div>
            </div>
            :
            <></>
        }
        {/* Bestand: die Klasse `colorTernaryLight` gibt es nicht — die Zeile
            läuft deshalb bewusst ohne eigene Farbe. */}
        {selectedPOITypes.indexOf('AdvertisementColumn') >= 0
            ?
            <div className="flex flex-wrap items-baseline gap-2">
                <div className="text-2xl font-bold">{advertisementColumns.length}</div><div className="text-lg">Litfaßsäulen</div>
            </div> :
            <></>
        }
    </div >;
}
